import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock3, Send, MessageCircle, Facebook, Twitter, Youtube, Instagram } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";
import { CONTACT, SOCIALS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = ["General Inquiry", "Advertising", "Distribution", "Content Partnership", "Careers", "Other"];
const ICONS = { facebook: Facebook, twitter: Twitter, youtube: Youtube, instagram: Instagram };
const INITIAL = { name: "", email: "", phone: "", subject: "General Inquiry", message: "" };

export default function Contact() {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent! Our team will get back to you soon.");
            setForm(INITIAL);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-white/10 bg-[#0F0F23] px-4 py-3.5 text-sm text-white placeholder-[#666688] outline-none transition-colors focus:border-[#C8102E]";

    return (
        <div data-testid="contact-page">
            <PageHero
                kicker="Contact Us"
                title="We'd love to"
                accent="hear from you"
                sub="Questions, advertising enquiries, distribution partnerships — drop us a line."
                crumb="Contact Us"
            />

            <section className="bg-[#0F0F23] pb-20 sm:pb-28">
                <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-5">
                    <Reveal className="lg:col-span-2">
                        <div className="gold-border-glow h-full rounded-3xl bg-[#16162B] p-8">
                            <Kicker>Reach Us</Kicker>
                            <ul className="mt-8 space-y-7 text-sm">
                                <li className="flex gap-4">
                                    <MapPin size={20} className="mt-0.5 shrink-0 text-[#C8102E]" />
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">Office</p>
                                        <p className="mt-1.5 leading-relaxed text-[#C9C9DE]">{CONTACT.address.map((l) => <span key={l} className="block">{l}</span>)}</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <Phone size={20} className="shrink-0 text-[#C8102E]" />
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">Phone</p>
                                        <a href={`tel:${CONTACT.phoneHref}`} data-testid="contact-phone" className="mt-1.5 block text-[#C9C9DE] transition-colors hover:text-[#FFA200]">{CONTACT.phone}</a>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <MessageCircle size={20} className="shrink-0 text-[#C8102E]" />
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">WhatsApp</p>
                                        <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="mt-1.5 block text-[#C9C9DE] transition-colors hover:text-[#FFA200]">{CONTACT.whatsapp}</a>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <Mail size={20} className="shrink-0 text-[#C8102E]" />
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">Email</p>
                                        <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="mt-1.5 block text-[#C9C9DE] transition-colors hover:text-[#FFA200]">{CONTACT.email}</a>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <Clock3 size={20} className="shrink-0 text-[#C8102E]" />
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">Office Hours</p>
                                        <p className="mt-1.5 text-[#C9C9DE]">{CONTACT.hours}</p>
                                    </div>
                                </li>
                            </ul>
                            <div className="mt-8 flex gap-3 border-t border-white/10 pt-7">
                                {SOCIALS.map((s) => {
                                    const Icon = ICONS[s.icon];
                                    return (
                                        <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name} data-testid={`contact-social-${s.icon}`} className="rounded-full border border-white/15 p-2.5 text-white/80 transition-all hover:border-[#C8102E] hover:bg-[#C8102E]">
                                            <Icon size={15} />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12} className="lg:col-span-3">
                        <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-white/10 bg-[#16162B] p-8">
                            <h2 className="font-display text-2xl font-black text-white">Send a message</h2>
                            <div className="mt-7 grid gap-5 sm:grid-cols-2">
                                <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="contact-name-input" className={inputCls} />
                                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address *" data-testid="contact-email-input" className={inputCls} />
                                <input value={form.phone} onChange={set("phone")} placeholder="Phone number" data-testid="contact-phone-input" className={inputCls} />
                                <select value={form.subject} onChange={set("subject")} data-testid="contact-subject-select" className={inputCls}>
                                    {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <textarea required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Your message *" data-testid="contact-message-input" className={`${inputCls} resize-none sm:col-span-2`} />
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="contact-submit-btn"
                                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-[#C8102E] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-[#8F0A20] disabled:opacity-60"
                            >
                                {sending ? "Sending…" : "Send Message"}
                                <Send size={15} className="transition-transform group-hover:translate-x-1" />
                            </button>
                        </form>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="mx-auto mt-12 max-w-7xl px-4 sm:px-6">
                    <div className="overflow-hidden rounded-3xl border border-white/10" data-testid="contact-map">
                        <iframe
                            title="Rongeen TV Office — New Town, Kolkata"
                            src="https://maps.google.com/maps?q=Curiocity%20Realtech%2C%20Mahammadpur%2C%20New%20Town%2C%20Kolkata%20700135&t=&z=14&ie=UTF8&iwloc=&output=embed"
                            className="h-80 w-full grayscale-[35%] invert-[90%] hue-rotate-180"
                            loading="lazy"
                        />
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
